import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Fab, IconButton } from '@mui/material'
import PageLoading from '../../../components/PageLoading/PageLoading'
import TableHeader from '../../../components/TableHeader/TableHeader'
import InformationTable from '../../../components/Table/InformationTable'
import { useNotification } from '../../../context/NotificationManager/NotificationManager'
import {
  createTemario,
  deleteTemario,
  getTemarioList,
  updateTemario
} from '../../../utils/api/materialDocente'
import { textFilter } from '../../../utils/table'
import ModalEditarTemario from '../../../modals/ModalEditarTemario/ModalEditarTemario'
import ModalConfirmacion from '../../../modals/ModalConfirmacion/ModalConfirmacion'
import { isRolBrain } from '../../../utils/auth'
import ModalNuevoTemario from '../../../modals/ModalNuevoTemario/ModalNuevoTemario'
import ModalAsignaturas from '../../../modals/ModalAsignaturas/ModalAsignaturas'
import ModalPdfViewer from '../../../modals/ModalPdfViewer/ModalPdfViewer'
import AsignaturaSelector from '../../../components/form/AsignaturaSelector/AsignaturaSelector'
import { pluralize } from '../../../utils/string'
import css from './Temario.module.css'

const Temario = () => {
  const queryClient = useQueryClient()
  const notification = useNotification()
  const [search, setSearch] = useState('')
  const [temarioSeleccionado, setTemarioSeleccionado] = useState(null)
  const [isOpenEditarTemario, setIsOpenEditarTemario] = useState(false)
  const [isOpenEliminarTemario, setIsOpenEliminarTemario] = useState(false)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isOpenAsignaturasModal, setIsOpenAsignaturasModal] = useState(false)
  const [isOpenPdfModal, setIsOpenPdfModal] = useState(false)
  const [selectedAsignatura, setSelectedAsignatura] = useState(null)

  const accesoBrain = isRolBrain()

  const { isLoading, data: temarioList = [] } = useQuery({
    queryKey: ['material', 'temario'],
    queryFn: () =>
      getTemarioList().catch(err => {
        notification.error({
          title: 'Error al recuperar los temarios',
          content: err
        })
        return []
      })
  })

  const { isPending: isTemarioCreating, mutate: crearTemario } = useMutation({
    mutationFn: createTemario,
    onSuccess: () => {
      notification.success({
        title: 'Temario enviado',
        content: 'Se ha enviado el temario'
      })
      queryClient.invalidateQueries({ queryKey: ['material', 'temario'] })
      setIsModalOpen(false)
    },
    onError: err => {
      notification.error({ title: 'Error al enviar el temario', content: err })
    }
  })

  const { isPending: isTemarioUpdating, mutate: actualizarTemario } =
    useMutation({
      mutationFn: updateTemario,
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['material', 'temario'] })
        notification.success({
          title: 'Temario actualizado',
          content: 'Datos modificados correctamente'
        })
        setIsOpenEditarTemario(false)
      },
      onError: err => {
        notification.error({
          title: 'Error actualizando el temario',
          content: err
        })
      }
    })

  const { isPending: isTemarioDeleting, mutate: borrarTemario } = useMutation({
    mutationFn: deleteTemario,
    onSuccess: () => {
      queryClient.invalidateQueries(['material', 'temario'])
      notification.success({
        title: 'Temario borrado',
        content: 'Se ha borrado el temario correctamente'
      })
      setTemarioSeleccionado(null)
      setIsOpenEliminarTemario(false)
      closeModal()
    },
    onError: err => {
      notification.error({ title: 'Error al borrar el temario', content: err })
    }
  })

  const handleNuevoTemario = ({ nombre, asignaturas, archivo, tipo }) => {
    if (isTemarioCreating) return
    crearTemario({ nombre, asignaturas, archivo, tipo })
  }

  const handleUpdateTemario = updatedTemario => {
    if (isTemarioUpdating) return
    actualizarTemario(updatedTemario)
  }

  const handleDeleteTemario = () => {
    if (isTemarioDeleting) return
    borrarTemario(temarioSeleccionado.id)
  }

  const filterSearch = temario => {
    const matchesSearch = textFilter({
      object: temario,
      fields: ['nombre'],
      search: search
    })
    const matchesAsignatura =
      !selectedAsignatura ||
      temario.asignaturas.some(
        asignatura => asignatura.id === selectedAsignatura.id
      )
    return matchesSearch && matchesAsignatura
  }

  const openModal = temario => {
    setTemarioSeleccionado(temario)
    setIsOpenEditarTemario(true)
  }

  const closeModal = () => {
    setIsOpenEditarTemario(false)
    setTemarioSeleccionado(null)
  }

  return (
    <PageLoading isLoading={isLoading}>
      <TableHeader
        title='Temario'
        search={search}
        onSearchChange={e => setSearch(e.target.value)}
        actions={
          <AsignaturaSelector
            label='Asignatura'
            value={selectedAsignatura?.id || null}
            onChange={(_event, newValue) => setSelectedAsignatura(newValue)}
            className={css.asignaturaFilter}
          />
        }
      />
      <InformationTable
        details={[
          { title: 'Nombre', key: 'nombre', sortKey: 'nombreTemario' },
          { title: 'Tipo de Prueba', key: 'tipo' },
          {
            title: 'Asignaturas',
            key: 'asignaturas',
            sortKey: 'totalAsignaturas'
          },
          { title: 'Opciones', key: 'opciones', align: 'center' }
        ]}
        data={temarioList.filter(filterSearch).map(temario => ({
          nombre: (
            <span
              className={css.link}
              onClick={() => {
                setTemarioSeleccionado(temario)
                setIsOpenPdfModal(true)
              }}
            >
              {temario.nombre || '--'}
            </span>
          ),
          nombreTemario: temario.nombre,
          tipo: temario.tipo || '--',
          totalAsignaturas: temario.asignaturas.length,
          asignaturas:
            temario.asignaturas.length > 0 ? (
              <span
                className={css.asignaturasCount}
                onClick={() => {
                  setTemarioSeleccionado(temario)
                  setIsOpenAsignaturasModal(true)
                }}
              >
                {temario.asignaturas.length}{' '}
                {pluralize('asignatura', temario.asignaturas.length)}
              </span>
            ) : (
              <span className={css.sinAsignaturas}>Sin asignaturas</span>
            ),
          opciones: (
            <>
              <IconButton
                size='small'
                color='secondary'
                disabled={!accesoBrain}
                onClick={() => openModal(temario)}
              >
                <i className='material-icons'>create</i>
              </IconButton>
              <IconButton
                size='small'
                color='error'
                disabled={!accesoBrain}
                onClick={() => {
                  setTemarioSeleccionado(temario)
                  setIsOpenEliminarTemario(true)
                }}
              >
                <i className='material-icons'>delete</i>
              </IconButton>
            </>
          )
        }))}
        isLoading={isLoading}
        isFetching={isLoading}
      />
      <ModalNuevoTemario
        open={isModalOpen}
        onSubmit={handleNuevoTemario}
        onClose={() => setIsModalOpen(false)}
        isLoading={isTemarioCreating}
      />
      <ModalEditarTemario
        open={isOpenEditarTemario}
        temario={temarioSeleccionado}
        onClose={closeModal}
        isLoading={isTemarioUpdating}
        onSubmit={handleUpdateTemario}
      />
      <ModalConfirmacion
        title='¿Seguro que quieres borrar el temario?'
        content='Al borrar el temario, dejará de estar disponible en el área de profesores.'
        open={isOpenEliminarTemario}
        onClose={() => setIsOpenEliminarTemario(false)}
        onClick={handleDeleteTemario}
      />
      <ModalAsignaturas
        open={isOpenAsignaturasModal}
        onClose={() => setIsOpenAsignaturasModal(false)}
        name={temarioSeleccionado?.nombre || ''}
        asignaturas={temarioSeleccionado?.asignaturas || []}
      />
      <ModalPdfViewer
        open={isOpenPdfModal}
        onClose={() => setIsOpenPdfModal(false)}
        title={temarioSeleccionado?.nombre || ''}
        pdfUrl={temarioSeleccionado?.url || ''}
      />
      {accesoBrain && (
        <Fab
          className={css.nuevo}
          onClick={() => setIsModalOpen(true)}
          size='medium'
          color='primary'
          aria-label='add'
        >
          <i className='material-icons'>add</i>
        </Fab>
      )}
    </PageLoading>
  )
}

export default Temario
